import { Message } from '../../interfaces/userRequest'
import { AccountOp } from '../accountOp/accountOp'
import { parse, stringify } from '../richJson/richJson'
import { humanizeCallWithModules, humanizerCallModules } from './callModules'
import {
  Erc7730CallDescriptors,
  Erc7730ResolvedDescriptor,
  humanizeCallWithErc7730,
  humanizeMessageWithErc7730
} from './erc7730'
import { IrCall, IrMessage } from './interfaces'
import {
  eip7702AuthorizationModule,
  ensMessageModule,
  entryPointModule,
  erc20Module,
  erc721Module,
  legendsMessageModule,
  openseaMessageModule,
  permit2Module,
  safeMessageModule,
  snapshotModule,
  zealyMessageModule
} from './messageModules'
import { fallbackShortPlaintext } from './messageModules/fallbackShortPlaintext'
import { dedupeWarnings } from './utils'

// from least generic to most generic
// the final visualization and warnings are from the first triggered module
const humanizerTMModules = [
  safeMessageModule,
  erc20Module,
  erc721Module,
  permit2Module,
  entryPointModule,
  legendsMessageModule,
  ensMessageModule,
  openseaMessageModule,
  zealyMessageModule,
  safeMessageModule,
  eip7702AuthorizationModule,
  snapshotModule,
  fallbackShortPlaintext
]

type HumanizeAccountOpOptions = {
  erc7730Descriptors?: Erc7730CallDescriptors
  nativeAssetSymbol?: string
}

type HumanizeMessageOptions = {
  erc7730Descriptor?: Erc7730ResolvedDescriptor
}

const humanizeAccountOp = (_accountOp: AccountOp, options?: HumanizeAccountOpOptions): IrCall[] => {
  const accountOp = parse(stringify(_accountOp))

  let currentCalls: IrCall[] = accountOp.calls.map((originalCall: IrCall) =>
    humanizeCallWithModules(accountOp, originalCall)
  )

  if (options?.erc7730Descriptors) {
    currentCalls = currentCalls.map((call, index) => {
      const resolvedDescriptor = options.erc7730Descriptors?.[index]
      if (!resolvedDescriptor || call.preferredOverErc7730) return call

      try {
        const originalCall = accountOp.calls[index]
        if (!originalCall) return call

        const erc7730Call = humanizeCallWithErc7730(
          originalCall,
          accountOp.chainId,
          accountOp.accountAddr,
          resolvedDescriptor,
          options.nativeAssetSymbol
        )
        if (!erc7730Call) return call

        // The descriptor builds its result from the raw call, so it starts with no warnings. The
        // warnings the modules found are still about the same call, so keep them both.
        return {
          ...erc7730Call,
          warnings: dedupeWarnings([...(call.warnings || []), ...(erc7730Call.warnings || [])])
        }
      } catch (error) {
        console.error(error)
        return call
      }
    })
  }

  return currentCalls
}

const humanizeMessage = (_message: Message, options?: HumanizeMessageOptions): IrMessage => {
  const message = parse(stringify(_message))

  try {
    // runs all modules and takes the first non empty array
    const { fullVisualization, warnings, canHideDropdownArrow } =
      humanizerTMModules
        .map((m) => {
          try {
            return m(message)
          } catch (error) {
            console.error(error)
            return {}
          }
        })
        .filter((p) => p.fullVisualization?.length)[0] || {}

    if (options?.erc7730Descriptor) {
      const erc7730Message = humanizeMessageWithErc7730(message, options.erc7730Descriptor)
      if (erc7730Message) {
        // The descriptor builds its result from the raw message, so it starts with no warnings.
        // The warnings humanizerTMModules found are still about the same message, so keep both.
        return {
          ...erc7730Message,
          warnings: dedupeWarnings([...(warnings || []), ...(erc7730Message.warnings || [])])
        }
      }
    }

    return { ...message, fullVisualization, warnings, canHideDropdownArrow }
  } catch (error) {
    console.error(error)
    return message
  }
}

export * from './erc7730'
export { humanizeAccountOp, humanizeMessage, humanizerCallModules }
export type { HumanizeAccountOpOptions, HumanizeMessageOptions }
